
import React, { useState, useCallback, useRef, useEffect, useMemo, Suspense } from 'react';
import type { NavSelection, BaseProblem, GeneratedProblem, ProblemParts, Worksheet, GenerationJob, AppView } from './types';
import { useAuth } from './hooks/useAuth';
import { useRecipeManager } from './hooks/usePrinciplesManager';
import { useExplanationGuidelinesManager } from './hooks/useExplanationGuidelinesManager';
import { useProblemManager } from './hooks/useProblemManager';
import Header from './components/Header';
import NavigationPane from './components/HistoryView';
import ContentPane from './components/BaseProblemHistoryView';
import { SettingsProvider, useSettings } from './contexts/SettingsContext';
import { SelectionProvider, useSelection } from './contexts/SelectionContext';
import { DialogProvider } from './contexts/DialogContext';
import Spinner from './components/Spinner';
import NaesinApp from './components/NaesinApp';
import type { AppMode } from './App';
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors, DragOverlay, type DragStartEvent, type DragEndEvent, closestCenter } from '@dnd-kit/core';
import MyPageView from './components/MyPageView';
import toast from 'react-hot-toast';

// --- Lazy Loading (Code Splitting) for Heavy Components ---
const RecipeModal = React.lazy(() => import('./components/RecipeModal'));
const ExplanationGuidelinesModal = React.lazy(() => import('./components/ExplanationGuidelinesModal'));
const SettingsModal = React.lazy(() => import('./components/SettingsModal'));
const PrintPreviewModal = React.lazy(() => import('./components/PrintPreviewModal').then(module => ({ default: module.PrintPreviewModal })));
const ContentEditorModal = React.lazy(() => import('./components/ContentEditorModal'));
const GenerateModifiedModal = React.lazy(() => import('./components/GenerateView'));
const GenerateCreativeModal = React.lazy(() => import('./components/CreativeGenerateView'));


const MIN_NAV_WIDTH = 200;
const MAX_NAV_WIDTH = 600;
const COLLAPSED_NAV_WIDTH = 80;


const DraggableFolderOverlay: React.FC<{ folder: { id: string, name: string } }> = ({ folder }) => (
    <div className="flex flex-col items-center justify-center p-4 bg-[var(--color-bg-secondary)] border border-[var(--color-accent)] rounded-lg cursor-grabbing h-48 w-48 opacity-75 pointer-events-none shadow-xl">
         <div className="w-24 h-20">
             <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
                 <path d="M3 11V6C3 4.89543 3.89543 4 5 4H11L13 6H19C20.1046 6 21 6.89543 21 8V11H3Z" fill="#52525b" />
                 <path d="M21 11H3V19.5C3 20.3284 3.67157 21 4.5 21H19.5C20.3284 21 21 20.3284 21 19.5V11Z" fill="#71717a" />
                 <path d="M21.5 11L19.5 21H4.5L2.5 11H21.5Z" fill="#a1a1aa" fillOpacity="0.9" />
             </svg>
        </div>
        <span className="mt-3 text-sm font-bold text-center truncate w-full">{folder.name}</span>
    </div>
);

const DraggableProblemOverlay: React.FC<{ problem: BaseProblem | GeneratedProblem, count: number }> = ({ problem, count }) => {
    const isGenerated = 'generatedParts' in problem;
    const parts = isGenerated ? problem.generatedParts : problem.parts;
    return (
      <div className="relative pointer-events-none">
        {count > 1 && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold z-20 border-2 border-[var(--color-bg-secondary)]">
                {count}
            </div>
        )}
        {count > 1 && (
            <div className="absolute top-2 left-2 w-96 h-full bg-[var(--color-bg-secondary)] border border-[var(--color-accent)] rounded-lg shadow-lg opacity-50 z-0"></div>
        )}
        <div className="p-4 bg-[var(--color-bg-secondary)] border border-[var(--color-accent)] rounded-lg shadow-lg opacity-90 w-96 flex items-center gap-3 relative z-10">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[var(--color-text-tertiary)]" viewBox="0 0 20 20" fill="currentColor">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            <div>
                <p className="text-sm font-bold text-[var(--color-text-primary)] truncate">{parts.lines[0]}</p>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                    {count > 1 ? `${count}개 문제 이동 중...` : '문제 이동 중...'}
                </p>
            </div>
        </div>
      </div>
    );
};

interface UndoAction {
  type: 'move' | 'reorder';
  undo: () => Promise<void>;
}

const AppContent: React.FC = () => {
  const [view, setView] = useState<AppView>('main');
  const [navSelection, setNavSelection] = useState<NavSelection>({ type: 'folder', id: '/' });
  
  // Modal States
  const [isRecipeModalOpen, setRecipeModalOpen] = useState(false);
  const [isGuidelinesModalOpen, setGuidelinesModalOpen] = useState(false);
  const [isSettingsModalOpen, setSettingsModalOpen] = useState(false);
  const [isPrintPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [isGenerateModifiedModalOpen, setGenerateModifiedModalOpen] = useState(false);
  
  // Content Action States
  const [problemsToPrint, setProblemsToPrint] = useState<(BaseProblem | GeneratedProblem)[]>([]);
  const [problemToEdit, setProblemToEdit] = useState<{ problem: BaseProblem | GeneratedProblem; isGenerated: boolean } | null>(null);
  const [jobToRetry, setJobToRetry] = useState<GenerationJob | null>(null);

  const [initialWorksheet, setInitialWorksheet] = useState<Worksheet | null>(null);
  const [navWidth, setNavWidth] = useState(280);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const isResizing = useRef(false);
  
  const [draggedFolder, setDraggedFolder] = useState<{ id: string, name: string } | null>(null);
  const [draggedProblem, setDraggedProblem] = useState<BaseProblem | GeneratedProblem | null>(null);
  const [problemToMove, setProblemToMove] = useState<{ id: string; isGenerated: boolean } | null>(null);
  const [isSelectionMoving, setIsSelectionMoving] = useState(false); // New state for bulk move mode

  const [isNavOpen, setIsNavOpen] = useState(false);

  const undoStack = useRef<UndoAction[]>([]);

  const { user, logout } = useAuth();
  const { 
    recipe, 
    addPrinciple, updatePrinciple, deletePrinciple, 
    addGeneralTool, updateGeneralTool, deleteGeneralTool,
    addSpecificTool, updateSpecificTool, deleteSpecificTool
  } = useRecipeManager();
  const { guidelines, addGuideline, updateGuideline, deleteGuideline } = useExplanationGuidelinesManager();
  
  const { koreanFontSize, mathFontSize, lineHeight, theme, isLoading: isSettingsLoading } = useSettings();
  
  // Use Selection Context
  const { selectedIds, toggleSelection, clearSelection, setSelection, isSelected } = useSelection();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const problemManager = useProblemManager(user?.uid, recipe, guidelines);
  const { baseProblems, problemHistory, onUpdateProblemOrder } = problemManager;

  // Keyboard listener for Undo (Ctrl+Z)
  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        const lastAction = undoStack.current.pop();
        if (lastAction) {
          await lastAction.undo();
          toast.success('실행 취소됨');
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Handle Browser Back Button (PopState)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.folderId) {
        setNavSelection({ type: 'folder', id: event.state.folderId });
        clearSelection();
      } else {
        // Default behavior or root
        if (window.location.pathname === '/') {
             setNavSelection({ type: 'folder', id: '/' });
             clearSelection();
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [clearSelection]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isResizing.current) {
      setNavWidth(prevWidth => {
        const newWidth = prevWidth + e.movementX;
        return Math.max(MIN_NAV_WIDTH, Math.min(newWidth, MAX_NAV_WIDTH));
      });
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
  }, []);
  
  const handleOpenPrintPreview = (problems: (BaseProblem | GeneratedProblem)[]) => {
    if (problems.length > 0) {
      setProblemsToPrint(problems);
      setInitialWorksheet(null); // Ensure it's a new worksheet
      setPrintPreviewOpen(true);
    } else {
        toast.error('선택된 문제가 없습니다.');
    }
  };

  // Wrapper for move problems with Undo support
  const handleMoveProblemWithUndo = async (id: string, isGenerated: boolean, targetFolder: string) => {
      const problem = (isGenerated ? problemHistory : baseProblems).find(p => p.id === id);
      const prevFolder = problem?.folder || ''; // Use existing folder or empty string for root/default

      await problemManager.onMoveProblem(id, isGenerated, targetFolder);

      undoStack.current.push({
          type: 'move',
          undo: async () => {
              await problemManager.onMoveProblem(id, isGenerated, prevFolder);
          }
      });
  };
  
  const handleMoveMultipleProblemsWithUndo = async (items: { id: string, isGenerated: boolean }[], targetFolder: string) => {
      const prevFolders: Record<string, string> = {};
      
      const movePromises = items.map(item => {
        const problem = (item.isGenerated ? problemHistory : baseProblems).find(p => p.id === item.id);
        if (problem) prevFolders[item.id] = problem.folder || '';
        return problemManager.onMoveProblem(item.id, item.isGenerated, targetFolder);
      });
      
      await Promise.all(movePromises);
      
      undoStack.current.push({
          type: 'move',
          undo: async () => {
              const restorePromises = items.map(item => {
                  const prevFolder = prevFolders[item.id];
                  return problemManager.onMoveProblem(item.id, item.isGenerated, prevFolder);
              });
              await Promise.all(restorePromises);
          }
      });
  }

  const handleNavSelect = useCallback((selection: NavSelection) => {
     if (problemToMove) {
        if (selection.type === 'folder') {
            handleMoveProblemWithUndo(problemToMove.id, problemToMove.isGenerated, selection.id);
            setProblemToMove(null);
            toast.success('문제가 이동되었습니다.');
        } else {
            toast.error('문제는 폴더로만 이동할 수 있습니다.');
        }
    } else if (isSelectionMoving && selectedIds.size > 0) {
         if (selection.type === 'folder') {
             const itemsToMove: { id: string, isGenerated: boolean }[] = [];
             selectedIds.forEach(id => {
                 const problem = [...baseProblems, ...problemHistory].find(p => p.id === id);
                 if(problem) itemsToMove.push({ id, isGenerated: 'generatedParts' in problem });
             });
             
             if (itemsToMove.length > 0) {
                 handleMoveMultipleProblemsWithUndo(itemsToMove, selection.id);
                 toast.success(`${itemsToMove.length}개 문제가 이동되었습니다.`);
             }
             
             setIsSelectionMoving(false);
             clearSelection();
             // Also navigate to the destination? Maybe better to stay and see they are gone?
             // Or follow standard OS behavior. Let's just move and stay.
         } else {
             toast.error('문제는 폴더로만 이동할 수 있습니다.');
         }
    } else {
        setNavSelection(selection);
        clearSelection();
        
        // Update Browser History for Folder Navigation
        if (selection.type === 'folder') {
            try {
                const url = new URL(window.location.href);
                url.searchParams.set('folder', selection.id);
                window.history.pushState({ folderId: selection.id }, '', url.toString());
            } catch (e) {
                // Ignore SecurityError in sandboxed environments (blob URLs)
                console.debug("History API not available in this environment");
            }
            problemManager.clearNewContentFolder(selection.id);
        }

        if (selection.type === 'worksheet') {
            const selectedWorksheet = problemManager.worksheets.find(ws => ws.id === selection.id);
            if (selectedWorksheet) {
                setInitialWorksheet(selectedWorksheet);
                setPrintPreviewOpen(true);
            }
        } else {
            setInitialWorksheet(null);
        }
    }
    setIsNavOpen(false);
  }, [problemToMove, isSelectionMoving, selectedIds, handleMoveProblemWithUndo, clearSelection, problemManager, baseProblems, problemHistory]);

  // Handle Mouse Back Button Navigation
  useEffect(() => {
    const handleMouseNavigation = (e: MouseEvent) => {
      if (e.button === 3) { // Mouse Back Button
        e.preventDefault();
        if (navSelection.type === 'folder' && navSelection.id !== '/') {
            // Calculate parent folder path
            const lastSlashIndex = navSelection.id.lastIndexOf('/');
            const parentPath = lastSlashIndex > 0 ? navSelection.id.substring(0, lastSlashIndex) : '/';
            handleNavSelect({ type: 'folder', id: parentPath });
        }
      }
    };

    window.addEventListener('mouseup', handleMouseNavigation);
    return () => window.removeEventListener('mouseup', handleMouseNavigation);
  }, [navSelection, handleNavSelect]);


  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);
  
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
      const { active } = event;
      const activeData = active.data.current;
      
      if (activeData?.type === 'folder') {
          setDraggedFolder({
              id: activeData.folderPath,
              name: activeData.folderPath.split('/').pop() || activeData.folderPath,
          });
      } else if (activeData?.type === 'problem') {
          const { problemId, isGenerated } = activeData;
          
          // If the dragged item is NOT in the current selection, select ONLY it (like standard OS behavior)
          if (!isSelected(problemId)) {
              setSelection(new Set([problemId]));
          }
          
          const problem = (isGenerated ? problemHistory : baseProblems).find(p => p.id === problemId);
          if (problem) {
              setDraggedProblem(problem);
          }
      }
  };


  const handleDragEnd = async (event: DragEndEvent) => {
    setDraggedFolder(null);
    setDraggedProblem(null);
    const { active, over } = event;
    if (!over) return;
    
    const activeData = active.data.current;
    const overData = over.data.current;

    // 1. Folder Move Logic
    if (activeData?.type === 'folder' && overData?.type === 'folder') {
        const draggedFolderPath = activeData.folderPath;
        const targetFolderPath = overData.folderPath;

        if (!draggedFolderPath || !targetFolderPath || draggedFolderPath === targetFolderPath) {
            return; // Invalid drop
        }

        // Prevent dropping a folder into its own subfolder
        if (targetFolderPath.startsWith(draggedFolderPath + '/')) {
            toast.error('폴더를 자신의 하위 폴더로 이동할 수 없습니다.');
            return;
        }
        
        const folderName = draggedFolderPath.split('/').pop();
        if(!folderName) return;

        const newPath = targetFolderPath === '/' ? folderName : `${targetFolderPath}/${folderName}`;
        
        problemManager.onRenameFolder(draggedFolderPath, newPath).then(() => toast.success('폴더가 이동되었습니다.'));
    } 
    
    // 2. Problem Move Logic (Drag problem to folder)
    else if (activeData?.type === 'problem' && overData?.type === 'folder') {
        const { folderPath } = overData;
        
        // Determine which items to move
        const itemsToMove: { id: string, isGenerated: boolean }[] = [];
        
        // Logic adjustment: Always move selected items if the active item is part of the selection.
        if (selectedIds.has(String(active.id))) {
            selectedIds.forEach(id => {
                const problem = [...baseProblems, ...problemHistory].find(p => p.id === id);
                if (problem) {
                    itemsToMove.push({ id: problem.id, isGenerated: 'generatedParts' in problem });
                }
            });
        } else {
             // Fallback (shouldn't be reached given handleDragStart logic)
             const { problemId, isGenerated } = activeData;
             itemsToMove.push({ id: problemId, isGenerated });
        }
        
        // Filter out items already in the target folder
        const itemsToActuallyMove = itemsToMove.filter(item => {
             const currentProblem = (item.isGenerated ? problemHistory : baseProblems).find(p => p.id === item.id);
             return (currentProblem?.folder || '/') !== folderPath;
        });
        
        if (itemsToActuallyMove.length > 0) {
            handleMoveMultipleProblemsWithUndo(itemsToActuallyMove, folderPath);
            toast.success(`${itemsToActuallyMove.length}개 문제가 이동되었습니다.`);
            clearSelection();
        }

    } 

    // 3. Problem Reordering Logic (Drag problem to problem)
    else if (activeData?.type === 'problem' && overData?.type === 'problem' && active.id !== over.id) {
        const activeIndex = activeData.orderIndex || 0;
        const overIndex = overData.orderIndex || 0;
        
        const epsilon = 0.0001; // Small offset to fit between integers
        let newOrderIndex = overIndex;
        
        // If ActiveIndex > OverIndex (Active was Higher/Earlier), we are moving Down/Later.
        // So we want a value slightly SMALLER than OverIndex.
        if (activeIndex > overIndex) {
            newOrderIndex = overIndex - epsilon;
        } else {
            newOrderIndex = overIndex + epsilon;
        }
        
        const { problemId, isGenerated } = activeData;
        await problemManager.onUpdateProblemOrder(problemId, isGenerated, newOrderIndex);
        toast.success("순서가 변경되었습니다.");
    }
  };

  const baseProblemForRetry = useMemo(() => {
    if (!jobToRetry || !jobToRetry.baseProblemId || !jobToRetry.baseProblemParts) {
        return [];
    }
    const existingProblem = baseProblems.find(p => p.id === jobToRetry.baseProblemId);
    if (existingProblem) {
        return [existingProblem];
    }
    const tempProblem: BaseProblem = {
        id: jobToRetry.baseProblemId,
        parts: jobToRetry.baseProblemParts,
        croppedImage: jobToRetry.baseProblemCroppedImage,
        createdAt: jobToRetry.createdAt,
        duplicateOf: null,
        explanation: null,
        deletedAt: null,
        folder: jobToRetry.folder
    };
    return [tempProblem];
  }, [jobToRetry, baseProblems]);

  // NEW LOGIC: Convert selected GeneratedProblems to BaseProblems format for generation
  const selectedProblemsForGeneration = useMemo(() => {
      const allProblems = [...baseProblems, ...problemHistory];
      const selected = allProblems.filter(p => selectedIds.has(p.id));
      
      return selected.map(p => {
          if ('generatedParts' in p) {
              // Map GeneratedProblem to a BaseProblem structure
              return {
                  id: p.id,
                  parts: p.generatedParts,
                  createdAt: p.createdAt,
                  explanation: p.explanation,
                  deletedAt: p.deletedAt,
                  folder: p.folder,
                  source: p.source,
                  coreIdeas: p.coreIdeas,
                  difficultyScore: p.difficultyScore,
                  // BaseProblem specific fields filled with defaults or available data
                  duplicateOf: null,
                  subject: null, 
                  majorUnit: null,
                  subUnit: null,
                  problemNumber: null,
                  croppedImage: null, 
                  originalFolder: null,
                  metadataStatus: 'completed'
              } as BaseProblem;
          }
          return p as BaseProblem;
      });
  }, [selectedIds, baseProblems, problemHistory]);


  // Helper function to handle request generation with toast
  const handleRequestGeneration = async (details: any) => {
    try {
        await problemManager.onRequestGeneration(details);
        toast.success('문제 생성이 요청되었습니다.');
        if (jobToRetry) {
            await problemManager.onDeleteJob(jobToRetry.id);
            setJobToRetry(null);
        }
    } catch (error) {
        console.error("Generation failed", error);
        toast.error("요청에 실패했습니다.");
    }
  };


  if (isSettingsLoading) {
    return (
        <div className="min-h-screen bg-[var(--color-bg-primary)] flex items-center justify-center">
            <Spinner message="사용자 설정 로딩 중..." />
        </div>
    );
  }
  
  return (
      <div className={`min-h-screen bg-[var(--color-bg-primary)] flex flex-col ${isPrintPreviewOpen ? 'print-preview-active' : ''}`}>
        <style>{`
          .sketchbook { 
            font-size: ${koreanFontSize}pt; 
            line-height: ${lineHeight};
          }
          .sketchbook mjx-container { 
            font-size: ${mathFontSize}pt !important; 
          }
        `}</style>
        <Header
          onOpenRecipe={() => setRecipeModalOpen(true)}
          onOpenGuidelines={() => setGuidelinesModalOpen(true)}
          onLogout={() => { logout(); toast.success('로그아웃 되었습니다.'); }}
          onSetView={setView}
          onOpenSettings={() => setSettingsModalOpen(true)}
          onToggleNav={() => setIsNavOpen(prev => !prev)}
        />
        {view === 'main' ? (
          <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
              <main className="flex-grow flex overflow-hidden relative">
                  {/* Mobile Navigation */}
                  <div className={`fixed inset-0 z-40 lg:hidden transition-opacity duration-300 ${isNavOpen ? 'bg-black/60' : 'bg-transparent pointer-events-none'}`} onClick={() => setIsNavOpen(false)}></div>
                  <div className={`absolute top-0 bottom-0 z-50 lg:static lg:z-auto transition-transform duration-300 ${isNavOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
                    <NavigationPane
                        style={{ width: isNavCollapsed ? `${COLLAPSED_NAV_WIDTH}px` : `${navWidth}px`, height: '100%', transition: 'width 0.2s ease-in-out' }}
                        isCollapsed={isNavCollapsed}
                        onToggleCollapse={() => setIsNavCollapsed(prev => !prev)}
                        baseProblems={problemManager.baseProblems}
                        problemHistory={problemManager.problemHistory}
                        generationJobs={problemManager.generationJobs}
                        trashedProblems={problemManager.trashedProblems}
                        worksheets={problemManager.worksheets}
                        allFolderPaths={problemManager.allFolderPaths}
                        currentSelection={navSelection}
                        onSelect={handleNavSelect}
                        onUpdateWorksheet={(id, data) => problemManager.onUpdateWorksheet(id, data).then(() => { toast.success('학습지가 수정되었습니다.'); })}
                        onDeleteWorksheet={(id) => problemManager.onDeleteWorksheet(id).then(() => { toast.success('학습지가 삭제되었습니다.'); })}
                        onRenameFolder={problemManager.onRenameFolder}
                        onDeleteFolder={(path) => problemManager.onDeleteFolder(path).then(() => { toast.success('폴더가 삭제되었습니다.'); })}
                        newContentFolders={problemManager.newContentFolders}
                    />
                  </div>
                  {!isNavCollapsed && (
                      <div 
                          className="w-1.5 cursor-col-resize bg-[var(--color-border)] hover:bg-[var(--color-accent)] transition-colors hidden lg:block"
                          onMouseDown={handleMouseDown}
                      />
                  )}
                  <ContentPane
                      selection={navSelection}
                      {...problemManager}
                      onOpenPrintPreview={handleOpenPrintPreview}
                      onSelect={handleNavSelect}
                      problemToMove={problemToMove}
                      setProblemToMove={setProblemToMove}
                      onMoveProblem={(id, isGen, folder) => handleMoveProblemWithUndo(id, isGen, folder).then(() => { toast.success('문제가 이동되었습니다.'); })}
                      setJobToRetry={setJobToRetry}
                      jobToRetry={jobToRetry}
                      setProblemToEdit={setProblemToEdit}
                      onOpenGenerateModifiedModal={() => setGenerateModifiedModalOpen(true)}
                      isSelectionMoving={isSelectionMoving}
                      setIsSelectionMoving={setIsSelectionMoving}
                  />
              </main>
               <DragOverlay>
                  {draggedFolder ? (
                      <DraggableFolderOverlay folder={draggedFolder} />
                  ) : draggedProblem ? (
                      <DraggableProblemOverlay problem={draggedProblem} count={selectedIds.has(draggedProblem.id) ? selectedIds.size : 1} />
                  ) : null}
              </DragOverlay>
          </DndContext>
        ) : (
          <MyPageView 
            onSetView={setView}
            baseProblems={baseProblems}
            problemHistory={problemHistory}
            generationJobs={problemManager.generationJobs}
            worksheets={problemManager.worksheets}
          />
        )}
        
        {/* Global Modals with Suspense */}
        <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center z-[9999]"><Spinner /></div>}>
            {isRecipeModalOpen && (
                <RecipeModal
                isOpen={isRecipeModalOpen}
                onClose={() => setRecipeModalOpen(false)}
                recipe={recipe}
                onAddPrinciple={addPrinciple}
                onUpdatePrinciple={updatePrinciple}
                onDeletePrinciple={deletePrinciple}
                onAddGeneralTool={addGeneralTool}
                onUpdateGeneralTool={updateGeneralTool}
                onDeleteGeneralTool={deleteGeneralTool}
                onAddSpecificTool={addSpecificTool}
                onUpdateSpecificTool={updateSpecificTool}
                onDeleteSpecificTool={deleteSpecificTool}
                />
            )}
            
            {isGuidelinesModalOpen && (
                <ExplanationGuidelinesModal
                isOpen={isGuidelinesModalOpen}
                onClose={() => setGuidelinesModalOpen(false)}
                guidelines={guidelines}
                onAddGuideline={addGuideline}
                onUpdateGuideline={updateGuideline}
                onDeleteGuideline={deleteGuideline}
                />
            )}

            {isSettingsModalOpen && (
                <SettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setSettingsModalOpen(false)}
                />
            )}

            {isPrintPreviewOpen && (
                <PrintPreviewModal
                isOpen={isPrintPreviewOpen}
                onClose={() => setPrintPreviewOpen(false)}
                baseProblems={baseProblems}
                problemHistory={problemHistory}
                problemsToDisplay={problemsToPrint}
                onUpdateProblemContent={problemManager.onUpdateProblemContent}
                onAddWorksheet={(ws) => problemManager.onAddWorksheet(ws).then(() => { toast.success('학습지가 저장되었습니다.'); })}
                initialWorksheet={initialWorksheet}
                />
            )}
            
            {problemToEdit && (
                <ContentEditorModal
                isOpen={!!problemToEdit}
                onClose={() => setProblemToEdit(null)}
                initialProblem={problemToEdit ? { 
                    parts: 'generatedParts' in problemToEdit.problem ? problemToEdit.problem.generatedParts : problemToEdit.problem.parts, 
                    explanation: problemToEdit.problem.explanation ?? null // Fix TS error: ensure undefined becomes null
                } : null}
                onSave={(newParts, newExplanation) => {
                    if (problemToEdit) {
                    problemManager.onUpdateProblemContent(problemToEdit.problem.id, problemToEdit.isGenerated, newParts, newExplanation).then(() => toast.success('문제가 수정되었습니다.'));
                    }
                }}
                />
            )}

            {jobToRetry && jobToRetry.modificationLevel === 4 && (
                <GenerateCreativeModal 
                    isOpen={!!jobToRetry}
                    onClose={() => setJobToRetry(null)}
                    onRequestGeneration={handleRequestGeneration}
                    jobToRetry={jobToRetry}
                    allFolderPaths={problemManager.allFolderPaths}
                />
            )}
            {jobToRetry && jobToRetry.modificationLevel !== 4 && (
                <GenerateModifiedModal
                    isOpen={!!jobToRetry}
                    onClose={() => setJobToRetry(null)}
                    baseProblems={baseProblemForRetry}
                    onRequestGeneration={handleRequestGeneration}
                    jobToRetry={jobToRetry}
                    allFolderPaths={problemManager.allFolderPaths}
                />
            )}
            {!jobToRetry && isGenerateModifiedModalOpen && (
                <GenerateModifiedModal
                    isOpen={isGenerateModifiedModalOpen}
                    onClose={() => setGenerateModifiedModalOpen(false)}
                    baseProblems={selectedProblemsForGeneration} 
                    onRequestGeneration={handleRequestGeneration}
                    jobToRetry={null}
                    allFolderPaths={problemManager.allFolderPaths}
                />
            )}
        </Suspense>
      </div>
  );
}

const MainApp: React.FC<{selectedMode: AppMode}> = ({ selectedMode }) => {
  useEffect(() => {
    // MathJax 3 processing
    if ((window as any).MathJax && (window as any).MathJax.typeset) {
      (window as any).MathJax.typeset();
    }
  }, []);

  if(selectedMode === 'naesin') {
    return <NaesinApp />;
  }

  return (
    <SettingsProvider>
      <SelectionProvider>
        <DialogProvider>
            <AppContent />
        </DialogProvider>
      </SelectionProvider>
    </SettingsProvider>
  );
};

export default MainApp;